import React from "react";
import { useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  MoreVertical,
  Search,
  Pencil,
  Trash2,
  CheckCircle,
  CircleAlert,
  FileText,
  Loader2,
} from "lucide-react";
import { useJobCode } from "@/hooks/userJobCode";
import { useInvoices } from "@/hooks/useInvoices";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { jobCodeSchema } from "@/schema/schema";
import LoadingIndicator from "@/components/LoadingIndicator";
import { JobCode, JobCodeFormData } from "@/types/jobCode";
import { invoiceList } from "@/types/invoices";
import { navigate } from "wouter/use-browser-location";
import { format } from "date-fns";
import { AxiosError } from "axios";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const JobCodeForm = ({
  initialData = null,
  onSubmit,
  isSubmitting,
}: {
  initialData?: JobCodeFormData | null;
  onSubmit: (data: JobCodeFormData) => void;
  isSubmitting: boolean;
}) => {
  const isExisting = !!initialData;
  const form = useForm<JobCodeFormData>({
    resolver: zodResolver(jobCodeSchema),
    defaultValues: initialData || {
      code: "",
      name: "",
      description: "",
    },
  });

  return (
    <div className="space-y-6 pt-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <Label required>Job Code</Label>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    className="h-10"
                    maxLength={128}
                    disabled={isExisting}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription className="text-sm text-muted-foreground">
                  A unique identifier used to tag invoices to a job.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <Label>Name</Label>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    className="h-10"
                    maxLength={300}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <Label>Description</Label>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    className="h-10"
                    maxLength={300}
                  />
                </FormControl>
                <FormDescription className="text-sm text-muted-foreground">
                  Maximum 300 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              className="flex-1 h-14 bg-[#0F172A] text-base font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Saving
                </>
              ) : initialData ? (
                "Update Job Code"
              ) : (
                "Register Job Code"
              )}
            </Button>
            <SheetClose asChild>
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-14 text-base font-semibold"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </SheetClose>
          </div>
        </form>
      </Form>
    </div>
  );
};

// Search hook, same pattern as the other masters
const useJobCodeSearch = (jobCodes: JobCode[] | undefined) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredJobCodes = React.useMemo(() => {
    if (!Array.isArray(jobCodes)) {
      return [];
    }
    return jobCodes.filter((jobCode) =>
      Object.values(jobCode).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [jobCodes, searchTerm]);

  return { searchTerm, setSearchTerm, filteredJobCodes };
};

// Panel that lists all invoices attached to a given job code
const JobInvoicesSheet = ({
  jobCode,
  invoices,
  open,
  onOpenChange,
}: {
  jobCode: JobCode | null;
  invoices: invoiceList[] | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { formatTotalAmount } = useDecimalPrecision();
  const relatedInvoices = React.useMemo(() => {
    if (!jobCode || !Array.isArray(invoices)) {
      return [];
    }
    return invoices.filter((invoice) => invoice.jobCode === jobCode.code);
  }, [jobCode, invoices]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[900px] sm:w-[900px] sm:max-w-[900px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Invoices for job code{" "}
            <span className="font-mono">{jobCode?.code}</span>
          </SheetTitle>
          <SheetDescription>
            {jobCode?.name || "—"} · {relatedInvoices.length} invoice(s).
            Click a row to open its details.
          </SheetDescription>
        </SheetHeader>

        <div className="pt-6">
          {relatedInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
              <FileText className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                No invoices are associated with this job code yet.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-800">
                <TableRow>
                  <TableHead>Invoice Number</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relatedInvoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/invoice-details/${invoice.typeCode}`, {
                        state: { id: invoice.id },
                      })
                    }
                  >
                    <TableCell className="font-medium">
                      {invoice.codeNumber}
                    </TableCell>
                    <TableCell>{invoice.buyerName ?? "Not Found"}</TableCell>
                    <TableCell>
                      {format(
                        new Date(invoice.docDate ?? invoice.invoiceDate),
                        "dd/MM/yyyy",
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTotalAmount(Number(invoice.totalPayableAmount))}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {invoice.isConsolidated
                          ? "Consolidated"
                          : (invoice.status ?? "Draft")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const JobList = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    jobCode: jobCodes,
    createJobCodeAsync,
    isCreating,
    updateJobCode,
    isUpdating,
    deleteJobCodeAsync,
    isLoading,
    error,
  } = useJobCode();
  const { invoices } = useInvoices();

  const [selectedJobCode, setSelectedJobCode] =
    React.useState<JobCodeFormData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [invoicesForJobCode, setInvoicesForJobCode] =
    React.useState<JobCode | null>(null);
  const { searchTerm, setSearchTerm, filteredJobCodes } = useJobCodeSearch(
    jobCodes,
  );

  // Invoice count per job code, for the table badge
  const invoiceCountByCode = React.useMemo(() => {
    const counts: Record<string, number> = {};
    invoices?.forEach((invoice) => {
      if (invoice.jobCode) {
        counts[invoice.jobCode] = (counts[invoice.jobCode] ?? 0) + 1;
      }
    });
    return counts;
  }, [invoices]);

  const cleanupBodyLock = () => {
    document.body.style.removeProperty("pointer-events");
    document.body.removeAttribute("data-scroll-locked");
  };

  if (isLoading) return <LoadingIndicator />;
  if (error) return <div>Error: {error.message}</div>;

  const handleSubmit = async (data: JobCodeFormData) => {
    let success = false;
    let toastTitle = "";
    let toastMessage = "";
    try {
      if (selectedJobCode) {
        await updateJobCode(
          {
            id: selectedJobCode.id,
            body: {
              code: data.code,
              name: data.name,
              description: data.description,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["jobCode"] });
              success = true;
              toastTitle = "Update Successful";
              toastMessage = "The job code has been successfully updated.";
            },
          },
        );
      } else {
        await createJobCodeAsync(data, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["jobCode"] });
            success = true;
            toastTitle = "Job Code Created";
            toastMessage = "The job code has been successfully created.";
          },
        });
      }
    } catch (err) {
      let message = "Failed to save job code. Please try again.";
      if (err instanceof AxiosError) {
        message = err.response?.data?.message || message;
      }
      toast({
        title: selectedJobCode ? "Error updating job code" : "Error creating job code",
        description: message,
        variant: "destructive",
        icon: <CircleAlert size={30} />,
      });
    } finally {
      if (success) {
        setIsSheetOpen(false);
        setSelectedJobCode(null);
        cleanupBodyLock();
        toast({
          title: toastTitle,
          description: toastMessage,
          duration: 5000,
          icon: (
            <CheckCircle size={30} className="animate-spin-once text-green-400" />
          ),
        });
      }
    }
  };

  const handleEdit = (jobCode: JobCode) => {
    setSelectedJobCode({
      id: jobCode.id,
      code: jobCode.code,
      name: jobCode.name,
      description: jobCode.description,
    });
    setIsSheetOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteJobCodeAsync(id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["jobCode"] });
          toast({
            title: "Success",
            description: "Job code has been deleted.",
            duration: 5000,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });
        },
      });
    } catch (err) {
      let message = "Failed to delete job code.";
      if (err instanceof AxiosError) {
        message = err.response?.data?.message || message;
      }
      toast({
        title: "Action failed",
        description: message,
        variant: "destructive",
        icon: <CircleAlert size={30} />,
      });
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search job codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-[300px] shadow-sm"
            />
          </div>
        </div>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button onClick={() => setSelectedJobCode(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Register Job Code
            </Button>
          </SheetTrigger>
          <SheetContent
            className="overflow-y-auto"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              cleanupBodyLock();
            }}
          >
            <SheetHeader>
              <SheetTitle>
                {selectedJobCode ? "Edit Job Code" : "Register New Job Code"}
              </SheetTitle>
              <SheetDescription>
                {selectedJobCode
                  ? "Edit the job code details below."
                  : "Fill in the job code details below."}
              </SheetDescription>
            </SheetHeader>
            <JobCodeForm
              initialData={selectedJobCode}
              onSubmit={handleSubmit}
              isSubmitting={isCreating || isUpdating}
            />
          </SheetContent>
        </Sheet>
      </div>

      <Table>
        <TableHeader className="bg-slate-50 dark:bg-slate-800">
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Invoices</TableHead>
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredJobCodes.map((jobCode) => (
            <TableRow key={jobCode.id}>
              <TableCell className="font-medium">{jobCode.code}</TableCell>
              <TableCell>{jobCode.name}</TableCell>
              <TableCell>{jobCode.description}</TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => setInvoicesForJobCode(jobCode)}
                >
                  <FileText className="h-4 w-4" />
                  <Badge variant="secondary">
                    {invoiceCountByCode[jobCode.code] ?? 0}
                  </Badge>
                </Button>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setInvoicesForJobCode(jobCode)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      View Invoices
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleEdit(jobCode)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(jobCode.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {filteredJobCodes.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                No job codes found. Register one to get started.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <JobInvoicesSheet
        jobCode={invoicesForJobCode}
        invoices={invoices}
        open={!!invoicesForJobCode}
        onOpenChange={(open) => {
          if (!open) {
            setInvoicesForJobCode(null);
            cleanupBodyLock();
          }
        }}
      />
    </div>
  );
};

export default JobList;
